
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, MessageSquare, Calendar, Building2, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface StartupCommunity {
  id: string;
  name: string;
  description: string;
  member_count: number;
  startup_accounts: {
    startup_name: string;
  };
  is_visible_to_teens: boolean;
}

const StartupCommunities: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [communities, setCommunities] = useState<StartupCommunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [joinedCommunities, setJoinedCommunities] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchCommunities();
    if (user) {
      fetchJoinedCommunities();
    }
  }, [user]);

  const fetchCommunities = async () => {
    try {
      const { data, error } = await supabase
        .from('startup_communities')
        .select(`
          id,
          name,
          description,
          member_count,
          is_visible_to_teens,
          startup_accounts!inner(startup_name)
        `)
        .eq('is_visible_to_teens', true)
        .order('member_count', { ascending: false });

      if (error) throw error;
      
      // Transform the data to match our interface
      const transformedData = data?.map(item => ({
        ...item,
        startup_accounts: Array.isArray(item.startup_accounts) 
          ? item.startup_accounts[0] 
          : item.startup_accounts
      })) || [];
      
      setCommunities(transformedData);
    } catch (error) {
      console.error('Error fetching communities:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJoinedCommunities = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('startup_community_members')
        .select('startup_community_id')
        .eq('user_id', user.id);

      if (error) throw error;
      setJoinedCommunities(new Set(data?.map(item => item.startup_community_id) || []));
    } catch (error) {
      console.error('Error fetching joined communities:', error);
    }
  };

  const handleJoinCommunity = async (communityId: string) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to join communities",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('startup_community_members')
        .insert({
          startup_community_id: communityId,
          user_id: user.id,
          role: 'member'
        });

      if (error) throw error;

      setJoinedCommunities(prev => new Set([...prev, communityId]));
      toast({
        title: "Successfully Joined!",
        description: "You can now participate in community discussions"
      });

      // Update member count locally
      setCommunities(prev => 
        prev.map(community => 
          community.id === communityId 
            ? { ...community, member_count: community.member_count + 1 }
            : community
        )
      );
    } catch (error) {
      console.error('Error joining community:', error);
      toast({
        title: "Failed to Join",
        description: "Please try again later",
        variant: "destructive"
      });
    }
  };

  const handleLeaveCommunity = async (communityId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('startup_community_members')
        .delete()
        .eq('startup_community_id', communityId)
        .eq('user_id', user.id);

      if (error) throw error;

      setJoinedCommunities(prev => {
        const newSet = new Set(prev);
        newSet.delete(communityId);
        return newSet;
      });

      toast({
        title: "Left Community",
        description: "You have left the community"
      });

      // Update member count locally
      setCommunities(prev => 
        prev.map(community => 
          community.id === communityId 
            ? { ...community, member_count: Math.max(0, community.member_count - 1) }
            : community
        )
      );
    } catch (error) {
      console.error('Error leaving community:', error);
      toast({
        title: "Failed to Leave",
        description: "Please try again later",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#FF8C00]">
            <Building2 className="h-5 w-5" />
            Startup Communities
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[#FF8C00]">
          <Building2 className="h-5 w-5" />
          Startup Communities
        </CardTitle>
        <CardDescription>
          Join communities created by innovative startups
        </CardDescription>
      </CardHeader>
      <CardContent>
        {communities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <p>No startup communities available yet</p>
            <p className="text-sm mt-2">
              Check back later for exciting new communities!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {communities.map((community) => (
              <div key={community.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-[#FF8C00]">{community.name}</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      by {community.startup_accounts.startup_name}
                    </p>
                    <p className="text-sm">{community.description}</p>
                  </div>
                  <Badge variant="outline" className="border-[#FF8C00] text-[#FF8C00]">
                    <Users className="h-3 w-3 mr-1" />
                    {community.member_count}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MessageSquare className="h-4 w-4" />
                      Chat Available
                    </span>
                  </div>
                  
                  {joinedCommunities.has(community.id) ? (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleLeaveCommunity(community.id)}
                        className="border-red-500 text-red-500 hover:bg-red-50"
                      >
                        Leave
                      </Button>
                      <Button
                        size="sm"
                        className="bg-[#FF8C00] hover:bg-orange-600 text-white"
                        onClick={() => window.open(`/startup-community/${community.id}`, '_blank')}
                      >
                        <MessageSquare className="h-4 w-4 mr-1" />
                        Chat
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleJoinCommunity(community.id)}
                      className="bg-[#FF8C00] hover:bg-orange-600 text-white"
                    >
                      Join Community
                    </Button>
                  )}
                </div>
              </div>
            ))}
            
            <div className="text-center pt-4">
              <p className="text-sm text-muted-foreground">
                Need help with communities? Contact{' '}
                <a 
                  href="mailto:teenvisor@gmail.com" 
                  className="text-[#FF8C00] hover:underline"
                >
                  teenvisor@gmail.com
                </a>
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StartupCommunities;
